/**
 * The meeting's own particulars: what is on the agenda, and who was there.
 *
 * These were previously only visible inside the edit form, which meant the user
 * entered them, closed the dialog, and could no longer see what they had entered
 * — every check ("did I mark Patel as attending by video?") required reopening a
 * form and risking an accidental change. Reading and editing are different jobs.
 *
 * Attendance is shown as it will be minuted: present in person and present by
 * video are distinct under S.173(2), and leave of absence is distinct from plain
 * absence because SS-1 requires it to be recorded.
 */

import { TbAlertTriangle, TbCrown } from 'react-icons/tb';

const ATTENDANCE_LABEL = {
  present: 'Present',
  video: 'By video',
  leave_of_absence: 'Leave of absence',
  absent: 'Absent',
};

/** Present in person and by video both count; the other two do not. */
const ATTENDANCE_TONE = {
  present: 'text-[#2c6045]',
  video: 'text-[#2c6045]',
  leave_of_absence: 'text-[#8a6d2f]',
  absent: 'text-[#a09786]',
};

export default function MeetingOverview({ meeting }) {
  const agenda = [...(meeting.agendaItems || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
  const attendees = meeting.attendees || [];
  const upsiCount = agenda.filter((a) => a.isUpsi).length;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* ── Agenda ──────────────────────────────────────────────────────── */}
      <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white p-5">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-[15px] font-bold text-[#2c2a24]">Agenda</h3>
          <span className="text-[11px] text-[#8a8172]">
            {agenda.length} {agenda.length === 1 ? 'item' : 'items'}
            {upsiCount > 0 && ` · ${upsiCount} flagged UPSI`}
          </span>
        </div>

        {agenda.length === 0 ? (
          <p className="text-[12px] text-[#8a8172]">
            No agenda items yet. The minutes cannot be drafted without at least one.
          </p>
        ) : (
          <ol className="space-y-1.5">
            {agenda.map((item, i) => (
              <li key={item._id || i} className="flex gap-2.5 rounded-lg bg-[#faf8f3] px-3 py-2">
                <span className="mt-px shrink-0 font-mono text-[11px] tabular-nums text-[#a09786]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="flex-1 text-[13px] leading-snug text-[#2c2a24]">{item.title}</span>
                {/* Flagged before the meeting; read after approval to propose
                    entries for the insider-trading register. */}
                {item.isUpsi && (
                  <span
                    className="mt-px shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold tracking-[0.5px] text-[#8a6d2f]"
                    style={{ background: '#8a6d2f1a' }}
                    title="Involves unpublished price sensitive information"
                  >
                    UPSI
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* ── Attendance ──────────────────────────────────────────────────── */}
      <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white p-5">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-[15px] font-bold text-[#2c2a24]">Attendance</h3>
          <span className="text-[11px] text-[#8a8172]">
            {attendees.length} {attendees.length === 1 ? 'director' : 'directors'}
          </span>
        </div>

        {attendees.length === 0 ? (
          <div className="flex gap-2 rounded-lg border border-[#e3c9a0] bg-[#fdf6e9] px-3 py-2.5">
            <TbAlertTriangle className="mt-0.5 shrink-0 text-[#9a5b1e]" size={15} />
            <p className="text-[12px] text-[#7a5a2a]">
              Nobody is marked as attending. The minutes need an attendance record and a
              chairperson before they can be issued.
            </p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {attendees.map((a, i) => (
              <li key={a._id || i} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-lg bg-[#faf8f3] px-3 py-2">
                <span className="text-[13px] font-semibold text-[#2c2a24]">{a.name}</span>
                {a.isChairperson && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#8a6d2f]">
                    <TbCrown size={11} /> CHAIR
                  </span>
                )}
                {a.din && <span className="text-[10px] text-[#a09786]">DIN {a.din}</span>}
                <span className={`ml-auto text-[11px] font-semibold ${ATTENDANCE_TONE[a.attendance] || ''}`}>
                  {ATTENDANCE_LABEL[a.attendance] || a.attendance}
                </span>
                {a.attendance === 'leave_of_absence' && a.leaveReason && (
                  <span className="w-full text-[11px] italic text-[#8a8172]">{a.leaveReason}</span>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* SS-1 requires the chair to be identified; the draft cannot name one
            that was never set, so the gap is surfaced before drafting. */}
        {attendees.length > 0 && !attendees.some((a) => a.isChairperson) && (
          <p className="mt-3 text-[11px] text-[#8a6d2f]">
            No chairperson is marked. Edit the meeting to set one — the minutes must name who chaired.
          </p>
        )}
      </div>
    </div>
  );
}
